import type { FileRecord } from "@cohesive/api-client";

/**
 * Path helpers for file-record transfers. Folders are not entities — a folder
 * is a record whose `path` ends in `"/"` — so a transfer destination is just a
 * folder-path prefix plus a leaf name, and collision handling is a caller-side
 * concern (the backend 409s on exact path collisions).
 */

/** Last path segment, without any trailing slash. `""` for empty/absent paths. */
export function leafNameFromPath(path: string | null | undefined): string {
  if (!path) return "";
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments[segments.length - 1] || cleaned;
}

/** Parent folder prefix of a path (`"a/b/c"` → `"a/b/"`); `""` for root-level. */
export function parentFolderPath(path: string | null | undefined): string {
  if (!path) return "";
  const cleaned = path.endsWith("/") ? path.slice(0, -1) : path;
  const segments = cleaned.split("/").filter(Boolean);
  return segments.length > 1 ? segments.slice(0, -1).join("/") + "/" : "";
}

/** Split a leaf into base + extension. A leading dot (`.env`) is part of the base. */
export function splitLeaf(leaf: string): { base: string; ext: string } {
  const dot = leaf.lastIndexOf(".");
  if (dot <= 0) return { base: leaf, ext: "" };
  return { base: leaf.slice(0, dot), ext: leaf.slice(dot) };
}

/**
 * Names of the entries directly inside `folderPath` (`""` = workspace root) —
 * what a copy into that folder can collide with. Includes both files and
 * subfolder names; excludes the folder marker itself and deeper descendants.
 */
export function folderLeafNames(
  records: FileRecord[],
  folderPath: string,
): Set<string> {
  const names = new Set<string>();
  for (const r of records) {
    if (!r.path || !r.path.startsWith(folderPath)) continue;
    const cleaned = r.path.endsWith("/") ? r.path.slice(0, -1) : r.path;
    const rest = cleaned.slice(folderPath.length);
    if (!rest) continue;
    const segments = rest.split("/").filter(Boolean);
    if (segments.length === 1) names.add(segments[0]);
  }
  return names;
}

/** Pick a non-colliding leaf, suffixing " (n)" before the extension. */
export function disambiguateLeaf(leaf: string, taken: Set<string>): string {
  if (!taken.has(leaf)) return leaf;
  const { base, ext } = splitLeaf(leaf);
  let n = 1;
  let candidate = `${base} (${n})${ext}`;
  while (taken.has(candidate)) {
    n += 1;
    candidate = `${base} (${n})${ext}`;
  }
  return candidate;
}
